import { Header, LayoutContainer, RteWrapper } from '@greystar/designsystem';
import {
  Fee,
  formatCurrency,
  getDictionaryEntry,
  useAnalytics,
  useFormattedAddress,
} from '@greystar/lib';
import { EventProperty } from '@greystar/lib/src/types/property-types';
import {
  RichText as JssRichText,
  Text as JssText,
  RichTextField,
  TextField,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FeeCategoryId, PropertyFeesProps, TableRow } from '../../../component-props';
import FeesTable from '../../FeesTable/FeesTable';
import Tabs from '../../Tabs/Tabs';
import { useI18n } from 'next-localization';

const getDisplayAmount = (item: Fee): string => {
  let feeText;

  if (item.amount) feeText = formatCurrency(item.amount, '$', 2);
  if (item.percentageAmount) feeText = `${item.percentageAmount}%`;
  if (item.textAmount) feeText = item.textAmount;
  if (item.valueType === 'range' && item.minAmount != null && item.maxAmount != null)
    feeText = `${formatCurrency(item.minAmount, '$', 2)} - 
      ${formatCurrency(item.maxAmount, '$', 2)}`;

  if (feeText) return `${feeText} ${item.providerMethod?.toLowerCase()}`;

  return '-';
};

const getFrequency = (item: Fee): string => {
  const frequency = item.frequency?.replaceAll('_', ' ').toLowerCase() ?? undefined;
  if (frequency === 'monthly') return 'Paid monthly';
  if (frequency === 'one time') return 'One-time payment';
  if (frequency === 'per occurrence') return 'Paid per occurrence';
  if (frequency) return `Paid ${frequency}`;

  return '';
};

const getFeeTableDataByCategory = (
  items: PropertyFeesProps['fees'],
  category: FeeCategoryId
): TableRow[] => {
  if (!items) return [];

  const filterList = items?.filter((item) => {
    const group = item.providerGroup;
    const freq = item.frequency;

    if (category === 'situational') return group === 'Situational';
    if (category === 'optional') return group === 'Personalized Add-Ons';
    if (category === 'oneTime')
      return (
        freq === 'one_time' &&
        group !== 'Situational' &&
        group !== 'Personalized Add-Ons' &&
        group !== 'Non-Customer Facing'
      );
    if (category === 'monthly')
      return (
        freq === 'monthly' &&
        group !== 'Situational' &&
        group !== 'Personalized Add-Ons' &&
        group !== 'Non-Customer Facing'
      );

    return false;
  });

  return filterList.map((item) => {
    const frequency = getFrequency(item);
    const displayAmount = getDisplayAmount(item);
    const item2 = frequency ? `${displayAmount}  (${frequency})` : displayAmount;
    return {
      ITEM_1: [item.label],
      ITEM_2: [item2],
      tooltipLabel: item.tooltipLabel ? [item.tooltipLabel] : undefined,
    };
  });
};

export const PropertyFees = (props: PropertyFeesProps): JSX.Element => {
  const [activeTabId, setActiveTabId] = useState<string>(
    props.tabConfiguration?.[0]?.fields.tabId.value ?? ''
  );
  const [activeFees, setActiveFees] = useState<TableRow[]>([]);
  const [activeDescription, setActiveDescription] = useState<RichTextField>();
  const [activeTabTitle, setActiveTabTitle] = useState<TextField>();
  const richTextRef = useRef<HTMLDivElement>(null);
  const analytics = useAnalytics();
  const { sitecoreContext } = useSitecoreContext();
  const property: EventProperty = sitecoreContext?.property ?? {};
  const propertyId = property.id ?? '';
  const propertyName = props.propertyName;
  const state = props.propertyLocation?.state;
  const property_location = useFormattedAddress(property.location);

  const { t } = useI18n();

  const onTabClick = (tabId: string) => {
    setActiveFees([]);
    setActiveTabId(tabId);
    trackTabClick(tabId);
  };

  const trackTabClick = useCallback(
    (tabId: string) => {
      try {
        const matchedTab = props.tabConfiguration?.find(
          (item) => item.fields.tabId.value === tabId
        );

        const tabTitle = matchedTab?.fields.tabTitle?.value ?? 'Unknown Tab';
        const eventData = {
          event: 'tab_click',
          action_type: 'tab click',
          state: state,
          property_location: property_location,
          property_id: propertyId,
          property_name: propertyName,
          content_group: 'fees tabs',
          module_name: 'fees section',
          inpage_location: 'middle',
          click_text: tabTitle,
          click_type: 'internal',
          destination_url: '#',
        };
        analytics.trackEvent('tab_click', eventData);
      } catch (error) {
        console.error('Error tracking tab click event:', error);
      }
    },
    [analytics, propertyId, propertyName, property_location, props.tabConfiguration, state]
  );

  const handleRichTextClick = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const anchorElement = target.closest('a');

      if (anchorElement) {
        try {
          const matchedTab = props.tabConfiguration?.find(
            (item) => item.fields.tabId.value === activeTabId
          );

          const tabTitle = matchedTab?.fields.tabTitle?.value ?? 'Unknown Tab';
          const eventData = {
            event: 'tab_click',
            action_type: 'tab click',
            state: state,
            property_location: property_location,
            property_id: propertyId,
            property_name: propertyName,
            content_group: 'fees description',
            module_name: 'fees section',
            inpage_location: 'middle',
            click_text: tabTitle,
            click_type: 'internal',
            destination_url: '#',
          };
          analytics.trackEvent('tab_click', eventData);
        } catch (error) {
          console.error('Error tracking Rich Text element click event:', error);
        }
      }
    },
    [activeTabId, propertyName]
  );

  const handleTooltipClick = useCallback(
    (label: string) => {
      const tabTitle =
        props.tabConfiguration?.find((tab) => tab.fields.tabId.value === activeTabId)?.fields
          .tabTitle?.value ?? '';

      analytics.trackEvent('tooltip', {
        action_type: 'click',
        state: state,
        property_location,
        property_id: propertyId,
        property_name: propertyName,
        content_group: tabTitle,
        module_name: 'fees section',
        inpage_location: 'middle',
        click_text: label,
        destination_url: '',
      });
    },
    [analytics, activeTabId, propertyId, propertyName, property_location, props.tabConfiguration]
  );

  useEffect(() => {
    const richTextCurrentRef = richTextRef.current;

    if (richTextCurrentRef) {
      richTextCurrentRef.addEventListener('click', handleRichTextClick);
    }

    return () => {
      if (richTextCurrentRef) {
        richTextCurrentRef.removeEventListener('click', handleRichTextClick);
      }
    };
  }, [handleRichTextClick]);

  useEffect(() => {
    if (!activeTabId || !props.tabConfiguration) {
      setActiveTabTitle(undefined);
      setActiveDescription(undefined);
      setActiveFees([]);
      return;
    }

    const activeTabDetails = props.tabConfiguration.find(
      (item) => item.fields.tabId.value === activeTabId
    );

    if (!activeTabDetails) {
      setActiveTabTitle(undefined);
      setActiveDescription(undefined);
      setActiveFees([]);
      return;
    }

    setActiveTabTitle(activeTabDetails.fields.tabTitle);
    setActiveDescription(activeTabDetails.fields.tabDescription);
    setActiveFees(getFeeTableDataByCategory(props.fees, activeTabId as FeeCategoryId));
  }, [activeTabId, props.fees, props.tabConfiguration]);

  return (
    <>
      {props.title && (
        <Header component="h2" id="fees">
          <JssText field={props.title} />
        </Header>
      )}
      {props.policiesTitle && props.policiesDescription && (
        <>
          <Header component="h4" as="h3" className="lg:mt-4">
            <JssText field={props.policiesTitle} />
          </Header>
          <RteWrapper>
            <p dangerouslySetInnerHTML={{ __html: props.policiesDescription }} />
          </RteWrapper>
        </>
      )}
      {props.disclosuresAndLicensesTitle && props.disclosuresAndLicensesDescription && (
        <>
          <Header component="h4" as="h3" className="lg:mt-4">
            <JssText field={props.disclosuresAndLicensesTitle} />
          </Header>
          <RteWrapper>
            <p dangerouslySetInnerHTML={{ __html: props.disclosuresAndLicensesDescription }} />
          </RteWrapper>
        </>
      )}
      <Header component="h4" as="h3" className="lg:mt-4">
        {props.pricingAndCostsTitle}
      </Header>
      <RteWrapper>
        <div ref={richTextRef} className="[&_div_p:last-child]:mb-0">
          <JssRichText field={props.pricingAndCostsDescription} />
        </div>
      </RteWrapper>
      <LayoutContainer flexDirection={'column'} gap={'sm'}>
        <Tabs
          tabList={props.tabConfiguration ?? []}
          activeTabId={activeTabId}
          onTabClick={onTabClick}
          backgroundColour={'beige'}
          prevLabel={getDictionaryEntry(t('tab-previous-fees'), 'Tab Previous Fees')}
          nextLabel={getDictionaryEntry(t('tab-next-fees'), 'Tab Next Fees')}
        />
        <FeesTable
          fees={activeFees}
          description={activeDescription?.value}
          header={activeTabTitle}
          toolTipClick={handleTooltipClick}
        />
      </LayoutContainer>
    </>
  );
};
